import { iconNavDots } from '@bsds-lib-component/icons/icons';
import { BsdsIcon } from '@bsds-lib-component/react';
import React, { useRef } from 'react';

import Tooltip from '../../tooltip/Tooltip';
import TableMenuPortal from './TableMenuPortal';
import { TABLE_MENU_TRIGGER_BUTTON_CLASS } from './tableMenuUtils';

type TableItemActionsMenuProps = {
  itemId: string | number;
  isOpen: boolean;
  menuId: string;
  triggerLabel: string;
  menuLabel: string;
  triggerTooltip?: string;
  menuPosition?: 'left' | 'right';
  disabled?: boolean;
  buttonClassName?: string;
  menuClassName?: string;
  onToggle: (event: React.MouseEvent) => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
  registerMenuRef: (element: HTMLDivElement | null) => void;
  registerButtonRef: (element: HTMLButtonElement | null) => void;
  children: React.ReactNode;
};

const TableItemActionsMenu: React.FC<TableItemActionsMenuProps> = ({
  itemId,
  isOpen,
  menuId,
  triggerLabel,
  menuLabel,
  triggerTooltip,
  menuPosition = 'left',
  disabled = false,
  buttonClassName,
  menuClassName = 'w-40',
  onToggle,
  onKeyDown,
  registerMenuRef,
  registerButtonRef,
  children,
}) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const triggerClassName = buttonClassName ?? TABLE_MENU_TRIGGER_BUTTON_CLASS;

  const setButtonRef = (element: HTMLButtonElement | null) => {
    buttonRef.current = element;
    registerButtonRef(element);
  };

  return (
    <>
      <Tooltip label={triggerTooltip ?? triggerLabel}>
        <button
          type="button"
          ref={setButtonRef}
          disabled={disabled}
          onClick={onToggle}
          onKeyDown={onKeyDown}
          className={`${triggerClassName} ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
          aria-label={triggerLabel}
          aria-expanded={isOpen}
          aria-haspopup="menu"
          aria-controls={menuId}
          aria-pressed={isOpen}
          aria-disabled={disabled}
        >
          <BsdsIcon icon={iconNavDots} className="w-3 h-3 text-gray-600" aria-hidden="true" />
        </button>
      </Tooltip>
      <TableMenuPortal
        isOpen={isOpen}
        anchorRef={buttonRef}
        menuRef={registerMenuRef}
        align={menuPosition}
        id={menuId}
        className={menuClassName}
        aria-label={menuLabel}
        data-item-id={itemId}
      >
        {children}
      </TableMenuPortal>
    </>
  );
};

export default TableItemActionsMenu;
